import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { userApi } from '../../services/api';
import { useAuthComplete } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { formatDate, getErrorMessage } from '../../utils/helpers';
import DeleteAccountModal from './DeleteAccountModal';

function ProfileView() {
  const { userId } = useParams();
  const { user: currentUser, isAuthenticated, logout } = useAuthComplete();
  const navigate = useNavigate();
  const toast = useToast();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (userId) {
        // Viewing another user's profile
        response = await userApi.getUserProfile(userId);
      } else {
        // Viewing own profile - use current user's ID
        if (!currentUser?.id) {
          throw new Error('You must be logged in to view your profile');
        }
        response = await userApi.getUserProfile(currentUser.id);
      }
      setUser(response.user);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    
    try {
      await userApi.deleteAccount();
      toast.success('Your account has been deleted successfully');
      logout();
      navigate('/');
    } catch (err) {
      console.error('Failed to delete account:', err);
      toast.error(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <h2 className="error-title">Profile Not Found</h2>
          <p className="error-message">{error}</p>
          <Link to="/" className="btn btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-error">
          <h2 className="error-title">Profile Not Found</h2>
          <p className="error-message">The user profile you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = isAuthenticated && currentUser && currentUser.id === user.id;
  const hasLinks = user.website || user.twitter || user.linkedin || user.github;

  return (
    <div className="profile-page">
      <div className="profile-card">
        <div className="profile-card-main">
          <div className="profile-left">
            <div className="profile-avatar">
              {user.displayName.charAt(0).toUpperCase()}
            </div>
            
            <div className="profile-info">
              <h1>{user.displayName}</h1>
              <p className="profile-meta">
                Member since {formatDate(user.createdAt)}
              </p>
              
              {user.bio && (
                <p className="profile-bio">{user.bio}</p>
              )}
            </div>
          </div>

          <div className="profile-right">
            {isOwnProfile && (
              <div className="profile-actions">
                <Link to="/profile/edit" className="btn btn-primary">
                  Edit Profile
                </Link>
                <Link to="/profile/change-password" className="btn btn-outline">
                  Change Password
                </Link>
                <button 
                  onClick={handleDeleteAccount}
                  className="btn btn-danger-outline"
                  aria-label="Delete account"
                >
                  Delete Account
                </button>
              </div>
            )}

            {hasLinks && (
              <div className="profile-links">
                <h4>Links</h4>
                <div className="links-list">
                  {user.website && (
                    <a
                      href={user.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      🌐 Website
                    </a>
                  )}
                  {user.twitter && (
                    <a
                      href={user.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      🐦 Twitter
                    </a>
                  )}
                  {user.linkedin && (
                    <a
                      href={user.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      💼 LinkedIn
                    </a>
                  )}
                  {user.github && (
                    <a
                      href={user.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      💻 GitHub
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="profile-stats">
          <h4>Community Stats</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{user.contentCount || 0}</span>
              <span className="stat-label">Published Content</span>
            </div>
          </div>
        </div>
      </div>
      
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default ProfileView;